export function isFullSentence(text: string): boolean {
  return text.endsWith(".") || text.endsWith("?") || text.endsWith("!");
}

/**
 * Returns the most recent sentence in `text` (the part after the last sentence
 * boundary), so a streaming caption refreshes one sentence at a time. A boundary
 * is a terminator followed by whitespace or an uppercase letter (the model
 * sometimes drops the space, e.g. "word2.Word1"); a following digit is ignored
 * so decimals like "3.5" don't split.
 */
export function currentSentence(text: string): string {
  const trimmedEnd = text.replace(/\s+$/, "");
  let lastBoundary = -1;
  for (let i = 0; i < trimmedEnd.length - 1; i++) {
    const char = trimmedEnd[i];
    const isTerminator = char === "." || char === "?" || char === "!";
    const next = trimmedEnd[i + 1];
    if (isTerminator && (/\s/.test(next) || /[A-Z]/.test(next))) {
      lastBoundary = i;
    }
  }
  return lastBoundary === -1 ? text.trimStart() : trimmedEnd.slice(lastBoundary + 1).trimStart();
}

/**
 * Given the full `text` and a character `count`, returns the text revealed up to
 * the last *complete* word. Used to pace a streaming caption word-by-word instead
 * of one character at a time (which reads as rushed). When `count` reaches the end
 * of the text, the full text is returned so the final word is never withheld.
 */
export function revealWholeWords(text: string, count: number): string {
  const clamped = Math.max(0, Math.min(count, text.length));
  if (clamped >= text.length) {
    return text;
  }
  // Already at a natural break (the next char starts a new word) — reveal as-is.
  if (/\s/.test(text[clamped])) {
    return text.slice(0, clamped).replace(/\s+$/, "");
  }
  // Mid-word: back up to the last word or sentence boundary so a partial word
  // isn't shown. Sentence terminators count as boundaries too, since the model
  // sometimes drops the trailing space (e.g. "word.Word").
  for (let i = clamped - 1; i >= 0; i--) {
    const char = text[i];
    if (/\s/.test(char) || char === "." || char === "?" || char === "!") {
      return text.slice(0, i + 1).replace(/\s+$/, "");
    }
  }
  return "";
}

export function toSnakeCase(str: string): string {
  return str
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1_$2")
    .toLowerCase();
}
